import { configUrls } from './clients/client-config';
import { getWorkstationNumber } from './workstation-util';

// TKT-000697. Counties report the app freezing and forcing a fresh sign-in, and there has
// been no way to look at it after the fact: reproducing it means catching it live in dev
// tools, and the reload that follows throws the evidence away.
//
// This posts one small record per interesting event - a session ending, a script or chunk
// failing to load, an uncaught error - so the question can be answered by querying afterwards.
//
// The chunk-error case is the one worth explaining. When a release lands, a browser holding a
// cached index.html can still be asking for chunk filenames that no longer exist. The lazy
// route never loads, the app sits there looking frozen, and a hard reload "fixes" it - which
// is exactly the folklore on these tickets. If that is what is happening at a county, these
// rows say so outright, with the bundle it was running and the URL that 404'd.
//
// SAFETY: nothing here may interfere with the app, least of all with signing in.
//   - sendBeacon is fire-and-forget; it cannot block, reject or be awaited
//   - every read is inside a try/catch, and callers pass nothing that needs evaluating first
//   - listeners are added, never assigned, so no existing handler is displaced, and the
//     events are not cancelled - error propagation is unchanged
//   - re-entrancy guard: an error raised while reporting can never report itself
//   - per-load budgets, so a render loop cannot flood anything
//   - the token is never sent, only its exp claim (a number)

// Errors repeat in bulk - a render loop, or a broken image on every row of a grid - so they
// get a small budget and are de-duplicated. Session events do not repeat that way, and how
// OFTEN they happen is the measurement this whole collection exists to make, so they get a
// large budget and are never de-duplicated.
const MAX_ERROR_EVENTS_PER_LOAD = 12;
const MAX_SESSION_EVENTS_PER_LOAD = 200;

const SESSION_EVENTS = ['login', 'reauth-required'];

// Mirrors NOW_THRESHOLD in store/auth/private.js getAuthIfValid, which treats a token as dead
// 20s before its exp. Using the same figure means 'expired' here means what the app means.
const NOW_THRESHOLD_MS = 20000;

let errorsThisLoad = 0;
let sessionEventsThisLoad = 0;
let reporting = false;
let installed = false;
const alreadySent = new Set();

// Which built bundle this browser is actually running. If a county is stuck on an old one,
// this is where it shows.
const appBuild = () => {
  const el = document.querySelector('script[src*="/js/app"]');
  return el && el.src ? el.src.split('/').pop().slice(0, 64) : undefined;
};

// Why the client gave up. Compared against the clock rather than inferred from presence.
//
// NOTE ON 'not-expired': it cannot currently be produced. The only caller is reAuth in
// store/auth/token.js, which runs after getAuthIfValid has already decided the token is dead
// using this same threshold against this same localStorage key, synchronously, so the token
// is always past expiry by the time we look. It is kept because it is the correct answer if
// a future caller reports a re-auth for some other reason (a 401 interceptor, say), and
// because the alternative is silently mislabelling such a session as 'expired'. Do not read
// an absence of 'not-expired' rows as evidence about anything - nothing can emit one yet.
const reauthReason = (exp) => {
  if (!exp) return 'missing';
  const expMs = Number(exp) * 1000;
  if (!Number.isFinite(expMs)) return 'missing';
  return expMs < Date.now() - NOW_THRESHOLD_MS ? 'expired' : 'not-expired';
};

// Test-only: clears the per-page-load budgets. Mirrors resetRateLimit in the auth service's
// sessionDiagnostic.js. Deliberately does NOT reset `installed`, so listener registration
// stays idempotent no matter how often this is called.
export const resetDiagnosticState = () => {
  errorsThisLoad = 0;
  sessionEventsThisLoad = 0;
  reporting = false;
  alreadySent.clear();
};

export const reportSessionEvent = (event, extra = {}) => {
  // an error thrown while reporting must not come back round and report itself
  if (reporting) return;
  reporting = true;
  try {
    const isSessionEvent = SESSION_EVENTS.indexOf(event) !== -1;
    let key;

    if (isSessionEvent) {
      if (sessionEventsThisLoad >= MAX_SESSION_EVENTS_PER_LOAD) return;
    } else {
      if (errorsThisLoad >= MAX_ERROR_EVENTS_PER_LOAD) return;
      // keyed on the source as well as the message, so two DIFFERENT chunks failing are
      // recorded as two rows rather than collapsing into one
      key = `${event}|${extra.message || ''}|${extra.source || ''}`;
      if (alreadySent.has(key)) return;
      // deliberately NOT marked seen yet - see the note where it is recorded below
    }

    const base = configUrls().AUTH_REST;
    if (!base) return;

    const exp = localStorage.getItem('auth.exp');
    const body = JSON.stringify({
      event,
      reason: event === 'reauth-required' ? reauthReason(exp) : undefined,
      message: extra.message,
      source: extra.source,
      username: localStorage.getItem('auth.user') || undefined,
      wkst: getWorkstationNumber() || undefined,
      tokenExp: Number(exp) || undefined,
      clientNow: Date.now(),
      sinceLoadMs: Math.round(
        (typeof performance !== 'undefined' && performance.now && performance.now()) || 0,
      ),
      appBuild: appBuild(),
      // separates "it died while she was working" from "she came back and it was logged out"
      visibility: typeof document !== 'undefined' ? document.visibilityState : undefined,
      // path only - never the query string
      route: `${window.location.pathname}${window.location.hash}`.slice(0, 128),
    });

    // text/plain, NOT application/json, and it matters. The app and the auth service are on
    // different hostnames at every county, so this is cross-origin; sendBeacon sends with
    // credentials mode "include", and a Content-Type that is not CORS-safelisted forces a
    // preflight the auth service's `Access-Control-Allow-Origin: *` cannot satisfy for a
    // credentialed request. The browser would answer the preflight and then silently never
    // send the POST, and we would collect nothing while believing this worked.
    const url = `${base.replace(/\/$/, '')}/session-diagnostic`;
    if (navigator && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }));
    } else {
      // only reached on a browser without sendBeacon, which is effectively none of ours
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body,
        keepalive: true,
      }).catch(() => {});
    }

    // Only now, once the body has actually been handed to a transport. Marking it earlier
    // meant that if we bailed before sending - no AUTH_REST resolved, say - that error
    // signature was blacklisted for the rest of the page's life and never recorded again.
    // For a tool whose whole job is to not lose evidence, that was the wrong way round.
    if (isSessionEvent) sessionEventsThisLoad += 1;
    else {
      errorsThisLoad += 1;
      alreadySent.add(key);
    }
  } catch (e) {
    // deliberately silent - a diagnostic must never surface to the user or break login
  } finally {
    reporting = false;
  }
};

// A stylesheet, or a preloaded/prefetched chunk, failing is the stale-bundle signature. A
// favicon, a manifest or an image failing is not, and must not be filed under the same name,
// or the query meant to confirm the stale-bundle theory fills up with 404'd favicons.
//
// rel="prefetch" MATTERS MOST here and is easy to miss. Vue CLI's preload plugin writes one
// prefetch link per async route chunk into index.html - 329 of them in this app's current
// production build. A browser holding a stale cached index.html after a release requests all
// of them on load, long before the user navigates anywhere, so those 404s are the earliest
// and loudest evidence that this is what is happening. Leaving prefetch out of this test
// filed every one of them as resource-error, burying the signal in the noise bucket that
// exists to keep it clean.
const isBundleResource = (tag, el) => {
  if (tag === 'SCRIPT') return true;
  if (tag !== 'LINK') return false;
  return /stylesheet|(^|\s)(module)?pre(load|fetch)(\s|$)/i.test(String(el.rel || ''));
};

export const installDiagnosticListeners = () => {
  try {
    if (installed || typeof window === 'undefined') return;
    installed = true;

    // Capture phase, because a failed script/stylesheet fires an error event on the ELEMENT
    // and those do not bubble.
    window.addEventListener('error', (e) => {
      try {
        const el = e && e.target;
        const tag = el && el !== window && el.tagName;
        if (tag) {
          const source = String(el.src || el.href || '').slice(0, 256);
          if (isBundleResource(tag, el)) {
            reportSessionEvent('chunk-error', { message: `${tag} failed to load`, source });
          } else {
            reportSessionEvent('resource-error', { message: `${tag} failed to load`, source });
          }
          return;
        }
        reportSessionEvent('js-error', {
          message: String((e && e.message) || 'unknown'),
          source: `${(e && e.filename) || ''}:${(e && e.lineno) || ''}`,
        });
      } catch (err) { /* silent */ }
    }, true);

    window.addEventListener('unhandledrejection', (e) => {
      try {
        const err = e && e.reason;
        const message = String((err && (err.message || err.name)) || err || 'unknown');
        // webpack raises ChunkLoadError when a lazy route's chunk 404s - precisely what a
        // stale cached index.html produces after a release
        const isChunk = /ChunkLoadError|Loading chunk|dynamically imported module/i.test(message);
        // an axios failure carries the URL on config; otherwise the stack is the only useful
        // locator. `.request` was tried here first and is an XMLHttpRequest, not a URL.
        // The first line of a stack repeats the message, and is the part most likely to carry
        // user-entered text if some code embedded it in the Error. Keep the frames, drop it.
        const frames = err && typeof err.stack === 'string'
          ? err.stack.split('\n').slice(1).join('\n').trim()
          : undefined;
        const source = (err && err.config && err.config.url) || frames || undefined;
        reportSessionEvent(isChunk ? 'chunk-error' : 'unhandled-rejection', {
          message: message.slice(0, 200),
          source: source ? String(source).slice(0, 256) : undefined,
        });
      } catch (err) { /* silent */ }
    });
  } catch (e) { /* silent */ }
};

export default { reportSessionEvent, installDiagnosticListeners, resetDiagnosticState };
